import { type ReactNode, useState } from "react";
import "./adminOptions.css"
import clsx from "clsx";
import Select, { type SingleValue } from "react-select";
import { _, _any } from "i18n";
import { AddLinkModal } from "../PhysicalAnalysis";
import { GameAnalysisPhysicalResponse } from "../../../../../portal/src/libs/apiClient";
import { formatSwappedState } from "../../utils/swappedState";
import { isKeepMappingResyncAllowed } from "../../utils/keepMappingResync";

const RESYNC_TYPE_TRANSFORMED = "transformed";
const RESYNC_TYPE_ALL = "all";
const RESYNC_TYPE_ALL_KEEP_MAPPING = "all_keep_mapping";

interface ResyncTypeOption {
    value: string;
    label: string;
}

interface GameAdminOptionsProps {
    toastMessage: (type: string, message: string) => void;
    analysisApproved?: boolean;
    leftSideStart?: string | null;
    syncRunning?: boolean;
    handleResync?: (resyncType: string | null, syncLocally: boolean | null, resyncAfter: string | null, skipReview: boolean) => void;
    handleAbortResync?: () => void;
    handleApprove?: (approvalStatus: "approved" | "disapproved") => Promise<boolean>;
    handleLeftSideStart?: (homeTeamStartsOnLeft: boolean | null) => void;
    physicalAnalysisReportData?: GameAnalysisPhysicalResponse;
    handlePostPhysicalAnalysisReportUrls?: (urls: string[]) => Promise<void>;
    /** Admin-only slot (e.g. video upload rename/delete). Not used on portal. */
    extraAdminOptions?: ReactNode;
    canSwapHomeAwayMapping?: boolean;
    isSwappingHomeAwayMapping?: boolean;
    handleSwapHomeAwayMapping?: () => void;
    swapped?: boolean | null;
    keepMappingAllowed?: boolean;
}

export function GameAdminOptions({
    toastMessage,
    syncRunning,
    analysisApproved,
    leftSideStart,
    handleResync,
    handleApprove,
    handleAbortResync,
    handleLeftSideStart,
    physicalAnalysisReportData,
    handlePostPhysicalAnalysisReportUrls,
    extraAdminOptions,
    canSwapHomeAwayMapping,
    isSwappingHomeAwayMapping,
    handleSwapHomeAwayMapping,
    swapped,
    keepMappingAllowed,
}: GameAdminOptionsProps) {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const defaultDateTime = `${year}-${month}-${day}T00:00`;
    const transformedResyncTypeOption: ResyncTypeOption = { value: RESYNC_TYPE_TRANSFORMED, label: _any(" label.resync_options.transformed") };
    const [approved, setApproved] = useState(analysisApproved ?? false);
    const [showResyncOptions, setShowResyncOptions] = useState(false);
    const [resyncType, setResyncType] = useState<SingleValue<ResyncTypeOption>>(transformedResyncTypeOption);
    const [syncLocally, setSyncLocally] = useState<SingleValue<{ value: boolean; label: string; }>>({ value: true, label: _any("app.eyeball.yes") });
    const [syncTime, setSyncTime] = useState(defaultDateTime);
    const [skipReview, setSkipReview] = useState<boolean>(false);
    const [showPhysicalAnalysis, setShowPhysicalAnalysis] = useState<boolean>(true);

    const onApproveButtonClick = async () => {
        if (handleApprove) {
            const success = await handleApprove(approved ? "disapproved" : "approved");

            if (success) {
                setApproved(!approved);
            }
        }
    }

    // Use the server prop, not local state. A swap flips homeTeamStartsOnLeft and
    // refetches game data; a stale toggle would invite a second swap that rolls back.
    const handleLeftStartClick = (side: string | null) => {
        let homeTeamStartsOnLeft = null;

        if (side === "home") {
            homeTeamStartsOnLeft = true;
        }
        if (side === "away") {
            homeTeamStartsOnLeft = false;
        }

        if (handleLeftSideStart) {
            handleLeftSideStart(homeTeamStartsOnLeft);
        }
    }

    // all_keep_mapping skips mapping, and mapping is what applies a swap. Hide it when the last
    // run reported swapped, or when a pending flip still needs mapping (abort keeps the starting
    // side and only clears resync, so swapped can still be the previous Sportlogiq answer).
    const isKeepMappingAllowed = isKeepMappingResyncAllowed(swapped, keepMappingAllowed);
    const keepMappingResyncTypeOption: ResyncTypeOption = {
        value: RESYNC_TYPE_ALL_KEEP_MAPPING,
        label: _("label.resync_options.all_keep_mapping"),
    };
    const resyncTypeOptions: ResyncTypeOption[] = [
        transformedResyncTypeOption,
        {
            value: RESYNC_TYPE_ALL,
            label: _(" label.resync_options.all"),
        },
        ...(isKeepMappingAllowed ? [keepMappingResyncTypeOption] : []),
    ];

    // Eligibility can load after the dropdown is open, and a pending flip can invalidate an
    // already picked all_keep_mapping. Fall back rather than keep an orphaned selection the
    // backend now rejects.
    const isSelectedResyncTypeAllowed = resyncType?.value !== RESYNC_TYPE_ALL_KEEP_MAPPING || isKeepMappingAllowed;
    const selectedResyncType = isSelectedResyncTypeAllowed ? resyncType : transformedResyncTypeOption;

    const handleResyncClick = () => {
        if (selectedResyncType && syncLocally && syncTime) {
            if (handleResync) {
                handleResync(selectedResyncType.value, syncLocally.value, syncTime, skipReview);
            }
        }
        else {
            toastMessage("error", "Fill all required fields")
        }
    }

    return (
        <div className="gameAdminOptions-container">
            {extraAdminOptions}
            <div className="adminOptionsItem">
                <div className="adminOptionsItem-label">{_("btn.analysis_approval")}: {approved ? _("status.approved") : _("status.notApproved")}</div>
                <button type="button" className="adminOptionsItem-button" onClick={onApproveButtonClick}>{approved ? _("label.approval.revokeApproval") : _("label.approval.approve")}</button>
            </div>
            <div className="adminOptionsItem">
                <div className="adminOptionsItem-label">{_("game.leftTeam.label")}</div>
                <div className="leftSideToggle">
                    <div className={clsx("toggleItem", { selected: leftSideStart === "home" })} onClick={() => handleLeftStartClick("home")}>{_("label.match.homeTeam")}</div>
                    <div className={clsx("toggleItem", { selected: leftSideStart === null })} onClick={() => handleLeftStartClick(null)}>{_("game.leftTeam.notSet")}</div>
                    <div className={clsx("toggleItem", { selected: leftSideStart === "away" })} onClick={() => handleLeftStartClick("away")}>{_("label.match.awayTeam")}</div>
                </div>
            </div>
            <div className="adminOptionsItem">
                <div className="adminOptionsItem-label">{_("label.resync")}:</div>
                <button type="button" className="adminOptionsItem-button" onClick={() => setShowResyncOptions(!showResyncOptions)}>{_("label.resync")}</button>
                {showResyncOptions &&
                    <div className="resyncOptions-container">
                        <div className="adminOptionsItem">
                            <div className="adminOptionsItem-label">{`${_("label.resyncType")}*:`}</div>
                            <Select
                                value={selectedResyncType}
                                classNamePrefix="react-select"
                                className="react-select"
                                options={resyncTypeOptions}
                                onChange={setResyncType}
                            />
                            <div className="resyncOptions-explanation">
                                <span className="icon-new icon-info-outline icon-16" />
                                <div className="explanationText">{_("label.resyncType.info")}</div>
                            </div>
                        </div>
                        <div className="adminOptionsItem">
                            <div className="adminOptionsItem-label">{`${_("label.syncLocally")}*:`}</div>
                            <Select
                                isDisabled={selectedResyncType?.value === RESYNC_TYPE_TRANSFORMED}
                                value={syncLocally}
                                classNamePrefix="react-select"
                                className="react-select"
                                options={[{ value: true, label: _("app.eyeball.yes") }, { value: false, label: _("app.eyeball.no") }]}
                                onChange={setSyncLocally}
                            />
                            <div className="resyncOptions-explanation">
                                <span className="icon-new icon-info-outline icon-16" />
                                <div className="explanationText">{_("label.syncLocally.info")}</div>
                            </div>
                        </div>
                        <div className="adminOptionsItem">
                            <div className="adminOptionsItem-label">{`${_("label.resyncAfter")}*:`}</div>
                            <input value={syncTime} onChange={e => setSyncTime(e.target.value)} className="resyncOptions-datetimeInput" type="datetime-local" />
                            <div className="resyncOptions-explanation">
                                <span className="icon-new icon-info-outline icon-16" />
                                <div className="explanationText">{_("label.resyncAfter.info")}</div>
                            </div>
                        </div>
                        <div className="adminOptionsItem">
                            <div>
                                <input onChange={e => setSkipReview(e.target.checked)} checked={skipReview} type="checkbox" id="skip-review" />
                                <label className="adminOptionsItem-label" htmlFor="skip-review">{`${_("label.skipReview")}*:`}</label>
                            </div>
                            <div className="resyncOptions-explanation">
                                <span className="icon-new icon-info-outline icon-16" />
                                <div className="explanationText">{_("label.skipReview.info")}</div>
                            </div>
                        </div>
                        <div className="resyncButtons">
                            <button type="button" className={clsx("resyncButton-abort", { enabled: syncRunning })} onClick={handleAbortResync}>{_("btn.abort_resync")}</button>
                            <button type="button" className="resyncButton-submit" onClick={handleResyncClick}>{_("label.resync")}</button>
                        </div>
                        {syncRunning &&
                            <div className="syncRunninngMessage">
                                <span className="icon-new icon-info-outline icon-16" />
                                {_("msg.resync_already_in_progress")}
                            </div>}
                    </div>
                }
            </div>
            {swapped !== undefined && (
                <div className="adminOptionsItem">
                    <div className="adminOptionsItem-label">{_("game.swapped.label")}: {formatSwappedState(swapped)}</div>
                </div>
            )}
            {canSwapHomeAwayMapping === true && (
                <div className="adminOptionsItem">
                    <div className="adminOptionsItem-label">{_("game.swapHomeAwayMapping.label")}</div>
                    <button
                        type="button"
                        className="adminOptionsItem-button"
                        disabled={isSwappingHomeAwayMapping === true}
                        onClick={handleSwapHomeAwayMapping}
                    >
                        {_("game.swapHomeAwayMapping.button")}
                    </button>
                </div>
            )}
            {analysisApproved && (
				<div className="adminOptionsItem">
					<AddLinkModal
						physicalAnalysisReportData={physicalAnalysisReportData}
						handlePostPhysicalAnalysisReportUrls={handlePostPhysicalAnalysisReportUrls}
                        toastMessage={toastMessage}
					/>
				</div>
			)}
        </div>
    )
}
